import React, { useState, useRef, useEffect } from 'react';

interface TimezoneSelectorProps {
  value: string;
  onChange: (timezone: string) => void;
  disabled?: boolean;
}

// Lista completa de fusos horários
const TIMEZONES = [
  // Brasil
  { value: 'America/Sao_Paulo', label: 'São Paulo', region: 'Brasil', offset: 'UTC-3' },
  { value: 'America/Manaus', label: 'Manaus', region: 'Brasil', offset: 'UTC-4' },
  { value: 'America/Rio_Branco', label: 'Rio Branco', region: 'Brasil', offset: 'UTC-5' },
  { value: 'America/Noronha', label: 'Fernando de Noronha', region: 'Brasil', offset: 'UTC-2' },
  { value: 'America/Belem', label: 'Belém', region: 'Brasil', offset: 'UTC-3' },
  { value: 'America/Fortaleza', label: 'Fortaleza', region: 'Brasil', offset: 'UTC-3' },
  { value: 'America/Recife', label: 'Recife', region: 'Brasil', offset: 'UTC-3' },
  { value: 'America/Bahia', label: 'Salvador', region: 'Brasil', offset: 'UTC-3' },
  { value: 'America/Cuiaba', label: 'Cuiabá', region: 'Brasil', offset: 'UTC-4' },
  { value: 'America/Campo_Grande', label: 'Campo Grande', region: 'Brasil', offset: 'UTC-4' },
  { value: 'America/Porto_Velho', label: 'Porto Velho', region: 'Brasil', offset: 'UTC-4' },
  { value: 'America/Boa_Vista', label: 'Boa Vista', region: 'Brasil', offset: 'UTC-4' },

  // América do Norte
  { value: 'America/New_York', label: 'Nova York', region: 'América do Norte', offset: 'UTC-5' },
  { value: 'America/Chicago', label: 'Chicago', region: 'América do Norte', offset: 'UTC-6' },
  { value: 'America/Denver', label: 'Denver', region: 'América do Norte', offset: 'UTC-7' },
  { value: 'America/Los_Angeles', label: 'Los Angeles', region: 'América do Norte', offset: 'UTC-8' },
  { value: 'America/Toronto', label: 'Toronto', region: 'América do Norte', offset: 'UTC-5' },
  { value: 'America/Vancouver', label: 'Vancouver', region: 'América do Norte', offset: 'UTC-8' },
  { value: 'America/Mexico_City', label: 'Cidade do México', region: 'América do Norte', offset: 'UTC-6' },

  // América do Sul
  { value: 'America/Buenos_Aires', label: 'Buenos Aires', region: 'América do Sul', offset: 'UTC-3' },
  { value: 'America/Santiago', label: 'Santiago', region: 'América do Sul', offset: 'UTC-3' },
  { value: 'America/Lima', label: 'Lima', region: 'América do Sul', offset: 'UTC-5' },
  { value: 'America/Bogota', label: 'Bogotá', region: 'América do Sul', offset: 'UTC-5' },
  { value: 'America/Caracas', label: 'Caracas', region: 'América do Sul', offset: 'UTC-4' },

  // Europa
  { value: 'Europe/London', label: 'Londres', region: 'Europa', offset: 'UTC+0' },
  { value: 'Europe/Paris', label: 'Paris', region: 'Europa', offset: 'UTC+1' },
  { value: 'Europe/Berlin', label: 'Berlim', region: 'Europa', offset: 'UTC+1' },
  { value: 'Europe/Madrid', label: 'Madrid', region: 'Europa', offset: 'UTC+1' },
  { value: 'Europe/Rome', label: 'Roma', region: 'Europa', offset: 'UTC+1' },
  { value: 'Europe/Lisbon', label: 'Lisboa', region: 'Europa', offset: 'UTC+0' },
  { value: 'Europe/Amsterdam', label: 'Amsterdã', region: 'Europa', offset: 'UTC+1' },
  { value: 'Europe/Brussels', label: 'Bruxelas', region: 'Europa', offset: 'UTC+1' },
  { value: 'Europe/Zurich', label: 'Zurique', region: 'Europa', offset: 'UTC+1' },
  { value: 'Europe/Moscow', label: 'Moscou', region: 'Europa', offset: 'UTC+3' },

  // Ásia
  { value: 'Asia/Dubai', label: 'Dubai', region: 'Ásia', offset: 'UTC+4' },
  { value: 'Asia/Shanghai', label: 'Xangai', region: 'Ásia', offset: 'UTC+8' },
  { value: 'Asia/Tokyo', label: 'Tóquio', region: 'Ásia', offset: 'UTC+9' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong', region: 'Ásia', offset: 'UTC+8' },
  { value: 'Asia/Singapore', label: 'Singapura', region: 'Ásia', offset: 'UTC+8' },
  { value: 'Asia/Seoul', label: 'Seul', region: 'Ásia', offset: 'UTC+9' },
  { value: 'Asia/Bangkok', label: 'Bangkok', region: 'Ásia', offset: 'UTC+7' },
  { value: 'Asia/Kolkata', label: 'Mumbai', region: 'Ásia', offset: 'UTC+5:30' },

  // Oceania
  { value: 'Australia/Sydney', label: 'Sydney', region: 'Oceania', offset: 'UTC+11' },
  { value: 'Australia/Melbourne', label: 'Melbourne', region: 'Oceania', offset: 'UTC+11' },
  { value: 'Pacific/Auckland', label: 'Auckland', region: 'Oceania', offset: 'UTC+13' },

  // África
  { value: 'Africa/Cairo', label: 'Cairo', region: 'África', offset: 'UTC+2' },
  { value: 'Africa/Johannesburg', label: 'Joanesburgo', region: 'África', offset: 'UTC+2' },
  { value: 'Africa/Lagos', label: 'Lagos', region: 'África', offset: 'UTC+1' },
];

export const TimezoneSelector: React.FC<TimezoneSelectorProps> = ({ value, onChange, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedTimezone = TIMEZONES.find(tz => tz.value === value);

  const filteredTimezones = TIMEZONES.filter(tz =>
    tz.label.toLowerCase().includes(search.toLowerCase()) ||
    tz.region.toLowerCase().includes(search.toLowerCase()) ||
    tz.offset.toLowerCase().includes(search.toLowerCase())
  );

  // Agrupar por região
  const groupedTimezones = filteredTimezones.reduce((acc, tz) => {
    if (!acc[tz.region]) acc[tz.region] = [];
    acc[tz.region].push(tz);
    return acc;
  }, {} as Record<string, typeof TIMEZONES>);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      inputRef.current?.focus();
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSelect = (timezone: string) => {
    onChange(timezone);
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="w-full flex items-center justify-between px-4 py-3 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700/50 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
      >
        <span className="flex items-center gap-2">
          <span className="text-base">🌍</span>
          {selectedTimezone ? (
            <>
              <span className="font-medium">{selectedTimezone.label}</span>
              <span className="text-gray-500 dark:text-gray-400">({selectedTimezone.offset})</span>
            </>
          ) : (
            <span className="text-gray-500">Selecione um fuso horário</span>
          )}
        </span>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-2 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-xl max-h-96 overflow-hidden">
          {/* Campo de busca */}
          <div className="sticky top-0 p-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-600">
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar cidade, região ou fuso..."
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>

          {/* Lista de timezones */}
          <div className="overflow-y-auto max-h-80">
            {Object.keys(groupedTimezones).length === 0 ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                Nenhum fuso horário encontrado
              </div>
            ) : (
              Object.entries(groupedTimezones).map(([region, timezones]) => (
                <div key={region}>
                  <div className="sticky top-0 px-4 py-2 bg-gray-50 dark:bg-gray-900 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {region}
                  </div>
                  {timezones.map((tz) => (
                    <button
                      key={tz.value}
                      type="button"
                      onClick={() => handleSelect(tz.value)}
                      className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors flex items-center justify-between ${
                        tz.value === value ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300' : 'text-gray-900 dark:text-white'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <span className="font-medium">{tz.label}</span>
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{tz.offset}</span>
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
